import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Progress, 
  Button, 
  Modal, 
  Form, 
  Input, 
  InputNumber, 
  Table, 
  Tag, 
  Space, 
  Typography, 
  Row, 
  Col, 
  Statistic,
  Alert,
  message,
  Tooltip
} from 'antd';
import { 
  CloudOutlined, 
  PlusOutlined, 
  ReloadOutlined, 
  DownloadOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import apiService from "services/api/apiService";

const { Title, Text } = Typography;
const { TextArea } = Input;

interface StorageUsage {
  id: number;
  tenant_id: string;
  total_storage_used: number;
  storage_limit: number;
  document_count: number;
  storage_used_mb: number;
  storage_limit_mb: number;
  usage_percentage: number;
  tenant_type: string;
  company_name: string;
  last_calculated: string;
}

interface StorageRequest {
  id: number;
  tenant_id: string;
  current_limit_mb: number;
  requested_limit_mb: number;
  requested_size_mb: number;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  requested_by: string;
  request_date: string;
  response_date?: string;
  notes?: string;
}

const StorageManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [storageData, setStorageData] = useState<StorageUsage | null>(null);
  const [requests, setRequests] = useState<StorageRequest[]>([]);
  const [requestModalVisible, setRequestModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchStorageData();
    fetchStorageRequests();
  }, []);

  const fetchStorageData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getStorageUsage();
      if (response.success) {
        setStorageData(response.data);
      } else {
        message.error('Failed to load storage data');
      }
    } catch (error) {
      message.error('Failed to load storage data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStorageRequests = async () => {
    try {
      const response = await apiService.getStorageRequests();
      if (response.success) {
        setRequests(response.data);
      }
    } catch (error) {
      console.error('Failed to load storage requests:', error);
    }
  };

  const calculateStorage = async () => {
    try {
      setLoading(true);
      const response = await apiService.calculateStorageUsage();
      if (response.success) {
        message.success('Storage usage recalculated');
        fetchStorageData();
      } else {
        message.error('Failed to calculate storage usage');
      }
    } catch (error) {
      message.error('Failed to calculate storage usage');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestMoreStorage = async (values: any) => {
    try {
      setLoading(true);
      const response = await apiService.requestMoreStorage({
        requested_size_mb: values.requested_size_mb,
        reason: values.reason,
        requested_by: storageData?.company_name || 'System User'
      });

      if (response.success) {
        message.success('Storage increase request submitted successfully');
        setRequestModalVisible(false);
        form.resetFields();
        fetchStorageRequests();
      } else {
        message.error(response.error || 'Failed to submit request');
      }
    } catch (error) {
      message.error('Failed to submit storage request');
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return '#ff4d4f';
    if (percentage >= 75) return '#faad14';
    return '#52c41a';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'green';
      case 'rejected': return 'red';
      case 'pending': return 'orange';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'Request Date',
      dataIndex: 'request_date',
      key: 'request_date',
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Current Limit',
      dataIndex: 'current_limit_mb',
      key: 'current_limit_mb',
      render: (size: number) => `${size} MB`
    },
    {
      title: 'Requested Size',
      dataIndex: 'requested_size_mb',
      key: 'requested_size_mb',
      render: (size: number) => `+${size} MB`
    },
    {
      title: 'New Limit',
      dataIndex: 'requested_limit_mb',
      key: 'requested_limit_mb',
      render: (size: number) => `${size} MB`
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true
    }
  ];

  if (!storageData) {
    return (
      <Card loading={loading}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <CloudOutlined style={{ fontSize: '48px', color: '#ccc' }} />
          <p>Loading storage information...</p>
        </div>
      </Card>
    );
  }

  const isNearLimit = storageData.usage_percentage >= 80;
  const isOverLimit = storageData.usage_percentage >= 100;
  const handleOptimizeStorage = async () => {
  try {
    setLoading(true);
    message.info('Preparing documents for download...');
    // Implementation will come later
    setTimeout(() => {
      message.success('Download started');
      setLoading(false);
    }, 2000);
  } catch (error) {
    message.error('Failed to download documents');
    setLoading(false);
  }
};

  return (
    <div>
      {/* Storage Overview */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card
            title={
              <Space>
                <CloudOutlined />
                Storage Management
              </Space>
            }
            extra={
              <Space>
                <Tooltip title="Recalculate storage usage">
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={calculateStorage}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                </Tooltip>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={() => setRequestModalVisible(true)}
                  disabled={storageData.tenant_type === 'trial'}
                >
                  Request More Storage
                </Button>
              </Space>
            }
          >
            {/* Storage Usage Alert */}
            {isOverLimit && (
              <Alert
                message="Storage Limit Exceeded"
                description="You have exceeded your storage limit. Please delete some files or request more storage."
                type="error"
                showIcon
                icon={<ExclamationCircleOutlined />}
                style={{ marginBottom: '16px' }}
              />
            )}
            {isNearLimit && !isOverLimit && (
              <Alert
                message="Storage Nearly Full"
                description="You are approaching your storage limit. Consider requesting more storage."
                type="warning"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}

            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="Storage Used"
                  value={storageData.storage_used_mb}
                  suffix={`MB`}
                  prefix={<CloudOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Storage Limit"
                  value={storageData.storage_limit_mb}
                  suffix="MB"
                  prefix={<FileTextOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Documents"
                  value={storageData.document_count}
                  prefix={<FileTextOutlined />}
                />
              </Col>
            </Row>

            <div style={{ marginTop: '24px' }}>
              <Text strong>Storage Usage: {storageData.usage_percentage}%</Text>
              <Progress
                percent={Math.min(storageData.usage_percentage, 100)}
                strokeColor={getProgressColor(storageData.usage_percentage)}
                style={{ marginTop: '8px' }}
              />
            </div>

            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <Text type="secondary">
                Last calculated: {new Date(storageData.last_calculated).toLocaleString()}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Storage Plan Information */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={12}>
          <Card title="Current Plan" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Account Type:</Text>
                <Tag color="blue" style={{ marginLeft: '8px' }}>
                  {storageData.tenant_type === 'hr_consultant' ? 'HR Consultant' : 'Direct Client'}
                </Tag>
              </div>
              <div>
                <Text strong>Base Storage:</Text>
                <Text style={{ marginLeft: '8px' }}>
                  {storageData.tenant_type === 'hr_consultant' ? '200 MB' : '100 MB'}
                </Text>
              </div>
              <div>
                <Text strong>Company:</Text>
                <Text style={{ marginLeft: '8px' }}>{storageData.company_name}</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Storage Options" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Button 
                  icon={<DownloadOutlined />} 
                  block
		  onClick={handleOptimizeStorage}
                >
                  Download All Documents
                </Button>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Save documents locally to free up cloud storage space
                </Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Storage Requests History */}
      <Card title="Storage Requests History">
        <Table
          columns={columns}
          dataSource={requests}
          rowKey="id"
          pagination={{
            pageSize: 5,
            showSizeChanger: false
          }}
          locale={{
            emptyText: 'No storage requests found'
          }}
        />
      </Card>

      {/* Request More Storage Modal */}
      <Modal
        title="Request More Storage"
        open={requestModalVisible}
        onCancel={() => {
          setRequestModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleRequestMoreStorage}
        >
          <Alert
            message="Current Storage Information"
            description={
              <div>
                <p>Current Limit: <strong>{storageData.storage_limit_mb} MB</strong></p>
                <p>Currently Used: <strong>{storageData.storage_used_mb} MB</strong></p>
                <p>Available: <strong>{(storageData.storage_limit_mb - storageData.storage_used_mb).toFixed(2)} MB</strong></p>
              </div>
            }
            type="info"
            style={{ marginBottom: '16px' }}
          />

          <Form.Item
            label="Additional Storage Needed"
            name="requested_size_mb"
            rules={[
              { required: true, message: 'Please enter the storage size needed' },
              { type: 'number', min: 10, max: 1000, message: 'Storage must be between 10MB and 1000MB' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter size in MB"
              suffix="MB"
              min={10}
              max={1000}
            />
          </Form.Item>

          <Form.Item
            label="Reason for Request"
            name="reason"
            rules={[
              { required: true, message: 'Please provide a reason for the storage request' },
              { min: 10, message: 'Please provide a detailed reason (minimum 10 characters)' }
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Please explain why you need additional storage space..."
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Submit Request
              </Button>
              <Button onClick={() => {
                setRequestModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StorageManagement;
